/**
 * Document Security Settings Component
 * 
 * Allows users to configure biometric authentication and PIN settings
 */

import { useState, useEffect } from 'react';
import { View, Alert, Switch, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ThemedText, SubHeading, Caption } from '@/components/ui/Text';
import {
  getAuthPreferences,
  setBiometricEnabled as setBiometricEnabledService,
  removePIN,
  checkBiometricAvailability,
  getBiometricTypeName,
} from '@/services/biometricAuth';
import { PINSetupModal } from './PINSetupModal';

export function DocumentSecuritySettings() {
  const theme = useTheme();
  const [biometricEnabled, setBiometricEnabledState] = useState(false);
  const [pinEnabled, setPinEnabled] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricType, setBiometricType] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showPINSetup, setShowPINSetup] = useState(false);

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      const preferences = await getAuthPreferences();
      setBiometricEnabledState(preferences.biometricEnabled);
      setPinEnabled(preferences.pinEnabled);
      setBiometricAvailable(preferences.biometricAvailable);

      if (preferences.biometricAvailable) {
        const availability = await checkBiometricAvailability();
        if (availability.types.length > 0) {
          setBiometricType(getBiometricTypeName(availability.types[0]));
        }
      }
    } catch (error) {
      console.error('[DocumentSecuritySettings] Error loading preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBiometricToggle = async (value: boolean) => {
    try {
      const success = await setBiometricEnabledService(value);
      if (success) {
        setBiometricEnabledState(value);
      } else {
        Alert.alert('Error', 'Failed to update biometric settings');
      }
    } catch (error) {
      console.error('[DocumentSecuritySettings] Error toggling biometric:', error);
      Alert.alert('Error', 'Failed to update biometric settings');
    }
  };

  const handleRemovePIN = () => {
    const doRemove = async () => {
      try {
        const success = await removePIN();
        if (success) {
          setPinEnabled(false);
          Alert.alert('Success', 'PIN has been removed');
        } else {
          Alert.alert('Error', 'Failed to remove PIN');
        }
      } catch (error) {
        console.error('[DocumentSecuritySettings] Error removing PIN:', error);
        Alert.alert('Error', 'Failed to remove PIN');
      }
    };

    // Web: Alert.alert can be flaky; use window.confirm
    if (Platform.OS === 'web' && typeof window !== 'undefined' && window.confirm) {
      const confirmed = window.confirm(
        'Remove PIN\n\nAre you sure you want to remove your PIN? You will need to set it up again to use PIN authentication.'
      );
      if (confirmed) void doRemove();
      return;
    }

    Alert.alert('Remove PIN', 'Are you sure you want to remove your PIN? You will need to set it up again to use PIN authentication.', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Remove', style: 'destructive', onPress: () => void doRemove() },
    ]);
  };

  const handlePINSetupComplete = () => {
    setShowPINSetup(false);
    loadPreferences(); // Reload to show PIN is now enabled
  };

  if (loading) {
    return null; // Or a loading indicator
  }

  return (
    <>
      <Card>
        <View style={{ gap: theme.spacing(3) }}>
          <View>
            <SubHeading style={{ marginBottom: theme.spacing(1) }}>
              Document Security
            </SubHeading>
            <Caption color={theme.colors.textSecondary}>
              Protect your documents with biometric authentication or a PIN
            </Caption>
          </View>

          {/* Biometric Authentication Setting */}
          {biometricAvailable && (
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                paddingVertical: theme.spacing(1),
              }}
            >
              <View style={{ flex: 1, marginRight: theme.spacing(2) }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1), marginBottom: theme.spacing(0.5) }}>
                  <Ionicons
                    // Use only valid Ionicons names; invalid names render as a "?" glyph.
                    // "scan-outline" reads well as Face ID / biometric scan.
                    name={biometricType?.includes('Face') ? 'scan-outline' : 'finger-print'}
                    size={20}
                    color={theme.colors.primary}
                  />
                  <ThemedText variant="bodyStrong">
                    {biometricType || 'Biometric Authentication'}
                  </ThemedText>
                </View>
                <Caption color={theme.colors.textSecondary} style={{ fontSize: 12 }}>
                  Use {biometricType || 'biometric authentication'} to access documents
                </Caption>
              </View>
              <Switch
                value={biometricEnabled}
                onValueChange={handleBiometricToggle}
                trackColor={{ false: theme.colors.borderMuted, true: theme.colors.primary + '80' }}
                thumbColor={biometricEnabled ? theme.colors.primary : theme.colors.textSecondary}
              />
            </View>
          )}

          {/* PIN Setting */}
          <View
            style={{
              paddingTop: theme.spacing(2),
              borderTopWidth: 1,
              borderTopColor: theme.colors.borderMuted,
              gap: theme.spacing(2),
            }}
          >
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <View style={{ flex: 1, marginRight: theme.spacing(2) }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1), marginBottom: theme.spacing(0.5) }}>
                  <Ionicons name="lock-closed" size={20} color={theme.colors.primary} />
                  <ThemedText variant="bodyStrong">PIN Authentication</ThemedText>
                </View>
                <Caption color={theme.colors.textSecondary} style={{ fontSize: 12 }}>
                  {pinEnabled
                    ? 'PIN is set and enabled'
                    : 'Set up a PIN to secure your documents'}
                </Caption>
              </View>
            </View>

            {pinEnabled ? (
              <Button
                label="Change PIN"
                variant="secondary"
                onPress={() => setShowPINSetup(true)}
                style={{ marginTop: theme.spacing(1) }}
              />
            ) : (
              <Button
                label="Set Up PIN"
                onPress={() => setShowPINSetup(true)}
                style={{ marginTop: theme.spacing(1) }}
              />
            )}

            {pinEnabled && (
              <Button
                label="Remove PIN"
                variant="ghost"
                onPress={handleRemovePIN}
                style={{ borderColor: theme.colors.error, borderWidth: 1 }}
                icon={<Ionicons name="trash-outline" size={18} color={theme.colors.error || '#FF3B30'} />}
              />
            )}
          </View>

          {/* Info Note */}
          <View
            style={{
              padding: theme.spacing(1.5),
              backgroundColor: theme.colors.primary + '08',
              borderRadius: theme.radii.md,
              flexDirection: 'row',
              alignItems: 'flex-start',
              gap: theme.spacing(1),
            }}
          >
            <Ionicons name="information-circle-outline" size={18} color={theme.colors.primary} />
            <Caption color={theme.colors.textSecondary} style={{ flex: 1, fontSize: 12 }}>
              At least one authentication method (biometric or PIN) should be enabled to secure your documents.
            </Caption>
          </View>
        </View>
      </Card>

      <PINSetupModal
        visible={showPINSetup}
        onComplete={handlePINSetupComplete}
        onCancel={() => setShowPINSetup(false)}
      />
    </>
  );
}

